import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, FileSpreadsheet, FileText, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { OVERVIEW_CARD_CLASSNAME } from "./constants";
import { ANALYTICS_TOOLBAR_BUTTON_CLASS } from "../shared/toolbarButtonStyles";

type TrackMetric = {
  label: string;
  value: number;
  color: string;
};

interface GuestPathTrackCardProps {
  metrics: TrackMetric[];
  ringValues: number[];
  completionRate: number;
  totalCompleted: number;
  totalStarted: number;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

function CircularTrack({
  values,
  metrics,
}: {
  values: number[];
  metrics: TrackMetric[];
}) {
  const size = 170;
  const center = size / 2;
  const baseRadius = 65;
  const strokeWidth = 8;
  const gap = 12;

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-[170px] w-[170px]">
      {values.map((value, index) => {
        const radius = baseRadius - index * gap;
        const circumference = 2 * Math.PI * radius;
        const dash = (value / 100) * circumference;
        const color = metrics[index]?.color ?? "#A78BFA";
        return (
          <g key={`${value}-${index}`}>
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="#F4F4F5"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${dash} ${circumference - dash}`}
              transform={`rotate(-90 ${center} ${center})`}
            />
          </g>
        );
      })}
    </svg>
  );
}

export const GuestPathTrackCard: React.FC<GuestPathTrackCardProps> = ({
  metrics,
  ringValues,
  completionRate,
  totalCompleted,
  totalStarted,
  onExport,
  exportDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn(OVERVIEW_CARD_CLASSNAME, "min-w-0")}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">
          {t("analytics.overview.guestPath.title", "Guest Path Track")}
        </h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={exportDisabled}>
            <Button
              type="button"
              variant="span"
              className={ANALYTICS_TOOLBAR_BUTTON_CLASS}
            >
              <Share className="size-3.5" />
              {t("analytics.common.export", "Export")}
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onExport?.("xlsx")}>
              <FileSpreadsheet className="size-4" />
              {t("analytics.common.xlsx", "XLSX")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onExport?.("csv")}>
              <FileText className="size-4" />
              {t("analytics.common.csv", "CSV")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col items-center">
        <CircularTrack values={ringValues} metrics={metrics} />
        <div className="mt-3 w-full min-w-0 space-y-2 px-0.5">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="flex min-w-0 items-center justify-between gap-3"
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: metric.color }}
                />
                <span className="truncate text-xs text-zinc-700">
                  {metric.label}
                </span>
              </div>
              <span className="shrink-0 text-xs font-medium text-zinc-800">
                {metric.value}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-zinc-600">
          {t("analytics.overview.guestPath.completionRate", "Completion Rate")}{" "}
          <span className="font-semibold text-[#7C3AED]">
            {completionRate}% (
            {t(
              "analytics.overview.guestPath.completionRateOf",
              "{{completed}} of {{started}}",
              { completed: totalCompleted, started: totalStarted },
            )}
            )
          </span>
        </p>
      </div>
    </div>
  );
};
